import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import generateManagerMembership from "../components/membership/generateManagerMembership";
import {
  ClubMode,
  ClubOption,
  NewClub,
  fetchAllClubs,
  fetchUserEmail,
  fetchUserId,
  joinClub as joinClubRequest,
  registerClub,
  saveUserEmail,
} from "../components/clubs/clubApi";
import ClubRegistrationFields, {
  EMPTY_CLUB,
  isClubComplete,
} from "../components/clubs/ClubRegistrationFields";
import { invalidateUserClubs } from "../components/clubs/useUserClubs";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PLAYER_STEPS = ["Club", "Name", "Email"];
const MANAGER_STEPS = ["Your details", "Your club"];

function OnboardingPage({
  mode,
  devPort,
  userName,
  setUserName,
  setCurrentMembership,
  betaMode = false,
}: {
  mode: ClubMode;
  devPort: string;
  userName: string;
  setUserName?: Dispatch<SetStateAction<string>>;
  setCurrentMembership?: Dispatch<SetStateAction<string>>;
  betaMode?: boolean;
}) {
  const navigate = useNavigate();
  const isManager = mode === "manager";
  const steps = isManager ? MANAGER_STEPS : PLAYER_STEPS;

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [userId, setUserId] = useState<number | null>(null);

  const [allClubs, setAllClubs] = useState<ClubOption[]>([]);
  const [selectedClub, setSelectedClub] = useState<ClubOption | null>(null);
  const [joinedWaitlist, setJoinedWaitlist] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [newClub, setNewClub] = useState<NewClub>(EMPTY_CLUB);

  useEffect(() => {
    if (!userName) {
      return;
    }
    Promise.all([
      fetchUserId(devPort, userName, mode),
      fetchUserEmail(devPort, userName),
      isManager ? Promise.resolve([]) : fetchAllClubs(devPort, betaMode),
    ])
      .then(([id, existingEmail, clubs]) => {
        if (!isManager && id === null) {
          navigate("/selectmembership");
          return;
        }
        setUserId(id);
        setEmail(existingEmail);
        setAllClubs(clubs);
        if (isManager && id !== null) {
          setActiveStep(1);
        }
        setIsLoading(false);
      })
      .catch(() => {
        setError("Something went wrong while loading your account. Please refresh the page.");
        setIsLoading(false);
      });
  }, [devPort, userName, mode, isManager, betaMode, navigate]);

  function joinClub() {
    if (!selectedClub || userId === null) {
      return;
    }
    setIsSubmitting(true);
    setError("");
    joinClubRequest(devPort, userId, selectedClub.value, firstName, lastName)
      .then((waitlist) => {
        invalidateUserClubs();
        setJoinedWaitlist(waitlist);
        setActiveStep(2);
      })
      .catch(() => setError("We couldn't join that club. Please try again."))
      .finally(() => setIsSubmitting(false));
  }

  function finishPlayerOnboarding() {
    if (!EMAIL_REGEX.test(email)) {
      setError("Please enter a valid email address");
      return;
    }
    setIsSubmitting(true);
    setError("");
    saveUserEmail(devPort, userName, email)
      .then(() => navigate("/playerclubselection"))
      .catch(() => {
        setError("We couldn't save your email. Please try again.");
        setIsSubmitting(false);
      });
  }

  function createManagerAccount() {
    if (!EMAIL_REGEX.test(email)) {
      setError("Please enter a valid email address");
      return;
    }
    setIsSubmitting(true);
    setError("");
    generateManagerMembership(
      devPort,
      userName,
      email,
      firstName.trim() + " " + lastName.trim(),
      setCurrentMembership,
      setUserName
    )
      .then(() => saveUserEmail(devPort, userName, email))
      .then(() => fetchUserId(devPort, userName, "manager"))
      .then((id) => {
        if (id === null) {
          throw new Error("Manager account was not created");
        }
        setUserId(id);
        setActiveStep(1);
      })
      .catch(() => setError("We couldn't create your manager account. Please try again."))
      .finally(() => setIsSubmitting(false));
  }

  function createClub() {
    if (userId === null) {
      return;
    }
    setIsSubmitting(true);
    setError("");
    registerClub(devPort, userId, newClub)
      .then(() => {
        invalidateUserClubs();
        navigate("/club");
      })
      .catch(() => {
        setError("We couldn't create your club. Please try again.");
        setIsSubmitting(false);
      });
  }

  const hasName = !!firstName.trim() && !!lastName.trim();

  let title: string;
  let subtitle: string;
  let content: ReactNode;
  let primaryAction: () => void;
  let primaryLabel = "Next";
  let primaryDisabled = false;
  let secondaryAction: (() => void) | null = null;
  let secondaryLabel = "";

  if (!isManager && activeStep === 0) {
    title = "Which club do you play at?";
    subtitle = "You can join more clubs later from your dashboard.";
    content = (
      <Autocomplete
        data-testid="onboarding-club"
        options={allClubs}
        value={selectedClub}
        onChange={(event, newValue) => setSelectedClub(newValue)}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderInput={(params) => <TextField {...params} label="Club Name" />}
      />
    );
    primaryAction = () => setActiveStep(1);
    primaryDisabled = !selectedClub;
  } else if (!isManager && activeStep === 1) {
    title = "What's your name?";
    subtitle = "This is how the club manager will see you on the player list.";
    content = (
      <Stack spacing={2}>
        <TextField
          inputProps={{ "data-testid": "onboarding-first-name" }}
          label="First Name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          autoFocus
        />
        <TextField
          inputProps={{ "data-testid": "onboarding-last-name" }}
          label="Last Name"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
        />
      </Stack>
    );
    primaryAction = joinClub;
    primaryDisabled = !hasName;
    secondaryAction = () => setActiveStep(0);
    secondaryLabel = "Back";
  } else if (!isManager && activeStep === 2) {
    title = "What's your email?";
    subtitle =
      "Stay updated on games and let your manager contact you.";
    content = (
      <Stack spacing={2}>
        {joinedWaitlist && selectedClub && (
          <Alert severity="info">
            {selectedClub.label.split(":")[0]} approves new players. You've been
            added to their approval list.
          </Alert>
        )}
        <TextField
          inputProps={{ "data-testid": "onboarding-email" }}
          label="Email Address"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoFocus
        />
      </Stack>
    );
    primaryAction = finishPlayerOnboarding;
    primaryLabel = "Finish";
    primaryDisabled = !email;
    secondaryAction = () => navigate("/playerclubselection");
    secondaryLabel = "Skip for now";
  } else if (isManager && activeStep === 0) {
    title = "Tell us about yourself";
    subtitle = "We use your email for billing and club notifications.";
    content = (
      <Stack spacing={2}>
        <TextField
          inputProps={{ "data-testid": "onboarding-first-name" }}
          label="First Name"
          value={firstName}
          onChange={(event) => setFirstName(event.target.value)}
          autoFocus
        />
        <TextField
          inputProps={{ "data-testid": "onboarding-last-name" }}
          label="Last Name"
          value={lastName}
          onChange={(event) => setLastName(event.target.value)}
        />
        <TextField
          inputProps={{ "data-testid": "onboarding-email" }}
          label="Email Address"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
      </Stack>
    );
    primaryAction = createManagerAccount;
    primaryDisabled = !hasName || !email;
  } else {
    title = "Register your club";
    subtitle = "You'll be able to set up your subscription from the dashboard.";
    content = (
      <ClubRegistrationFields club={newClub} onChange={setNewClub} autoFocus />
    );
    primaryAction = createClub;
    primaryLabel = "Create club";
    primaryDisabled = !isClubComplete(newClub);
  }

  return (
    <Layout managerMode={-2} devPort={devPort}>
      <Box sx={{ maxWidth: 560, mx: "auto", px: 2, py: 3 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{ textAlign: "center", mb: 3, fontWeight: 600, color: "white" }}
        >
          {isManager ? "Set up your club" : "Welcome to Chukkerbook"}
        </Typography>
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          }}
        >
          {(isLoading || isSubmitting) && <LinearProgress color="secondary" />}
          <CardContent sx={{ p: 3 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            {!isLoading && (
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {subtitle}
                  </Typography>
                </Box>
                {error && <Alert severity="error">{error}</Alert>}
                {content}
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="space-between"
                  sx={{ pt: 1 }}
                >
                  {secondaryAction ? (
                    <Button
                      onClick={secondaryAction}
                      disabled={isSubmitting}
                      color="inherit"
                      data-testid="onboarding-secondary"
                    >
                      {secondaryLabel}
                    </Button>
                  ) : (
                    <span />
                  )}
                  <Button
                    variant="contained"
                    size="large"
                    onClick={primaryAction}
                    disabled={primaryDisabled || isSubmitting}
                    data-testid="onboarding-next"
                    sx={{ px: 4, borderRadius: 2, fontWeight: 600 }}
                  >
                    {primaryLabel}
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      </Box>
    </Layout>
  );
}

export default OnboardingPage;
